// Canonical long-form document page template.

import React, { useMemo } from 'react';
import {
  getReportGradientAccentFamily,
  type Document,
} from '@airhead-games/shared/content';
import { ASSETS, gameLibrary } from '@airhead-games/shared/configs';
import { findGameDiscoverySlug } from '@airhead-games/shared/utilities';
import ArticleMetaSection from '@/content/rendering/chrome/ArticleMetaSection';
import Breadcrumbs from '@/content/rendering/chrome/Breadcrumbs';
import DocumentHero from '@/content/rendering/chrome/DocumentHero';
import NavigationRail from '@/content/rendering/chrome/NavigationRail';
import WidgetRenderer from '@/content/rendering/renderers/WidgetRenderer';
import { getDocumentBreadcrumbs } from '@/content/lib/breadcrumbs';
import { useContentHashNavigation } from '@/content/lib/hashNavigation';
import { buildOutline } from '@/content/lib/outline';
import { getDocumentImage } from '@/content/lib/seo';
import DocumentSectionTree from '@/content/rendering/sections/DocumentSectionTree';
import { Separator } from '@/components/ui/separator';
import SEO, { type ArticleData, type GameData } from '@/seo/SEO';
import { getColorStop } from '@/styles/playerColors';

interface DocumentTemplateProps {
  document: Document;
}

const getReportAccent = (document: Document) => {
  const family = getReportGradientAccentFamily(document.meta?.reportMonth);
  return getColorStop(family, '500');
};

const getArticleData = (document: Document): ArticleData | undefined => {
  if (document.kind !== 'blog' && document.kind !== 'report') {
    return undefined;
  }

  const image = getDocumentImage(document) ?? ASSETS.ogImage;

  return {
    headline: document.seo.title,
    description: document.seo.description,
    image,
    datePublished:
      document.meta?.publishedAt?.toISOString() ??
      document.meta?.updatedAt?.toISOString() ??
      new Date(0).toISOString(),
    dateModified: document.meta?.updatedAt?.toISOString(),
    author: {
      name: document.meta?.author ?? 'Jay',
      email: document.kind === 'blog' ? 'poppy@airheadgames.com' : undefined,
    },
  };
};

const getGameData = (document: Document): GameData | undefined => {
  if (document.kind !== 'game') {
    return undefined;
  }

  const title = document.seo.title.replace(/^Discover\s+/, '');
  const game = gameLibrary.find(
    (entry) => findGameDiscoverySlug(entry.gameCore.gameId) === document.slug
  );

  if (!game) {
    return undefined;
  }

  const duration =
    typeof game.gameWiki.averageDuration === 'number'
      ? `~${game.gameWiki.averageDuration} minutes`
      : undefined;

  return {
    name: title,
    description: document.seo.description,
    image: getDocumentImage(document) ?? ASSETS.ogImage,
    slug: document.slug,
    playersMin: game.gameCore.gamePlayersMin,
    playersMax: game.gameCore.gamePlayersMax,
    gameType: game.gameWiki.gameType,
    duration,
    datePublished: game.gameWiki.releasedAt?.toISOString(),
  };
};

const DocumentTemplate: React.FC<DocumentTemplateProps> = ({ document }) => {
  useContentHashNavigation();

  const articleActionsSection = useMemo(
    () =>
      document.kind === 'blog'
        ? document.sections.find((section) => section.id === 'article-actions')
        : undefined,
    [document]
  );
  const contentDocument = useMemo(
    () =>
      articleActionsSection
        ? {
            ...document,
            sections: document.sections.filter(
              (section) => section.id !== articleActionsSection.id
            ),
          }
        : document,
    [articleActionsSection, document]
  );

  const breadcrumbs = useMemo(
    () => getDocumentBreadcrumbs(document),
    [document]
  );
  const outline = useMemo(
    () => buildOutline(contentDocument.sections),
    [contentDocument.sections]
  );
  const hasArticleMeta = document.kind === 'blog' || document.kind === 'report';

  const hasToc = Boolean(document.aside?.showToc && outline.length > 0);
  const contentLayoutClassName = hasToc
    ? 'grid gap-3 lg:gap-8 lg:grid-cols-[13rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)] 2xl:grid-cols-[17rem_minmax(0,1fr)]'
    : 'w-full';
  const reportAccent =
    document.kind === 'report' ? getReportAccent(document) : undefined;
  const reportPageStyle =
    document.kind === 'report' && reportAccent
      ? ({
          '--report-accent': reportAccent,
          backgroundColor:
            'color-mix(in oklab, var(--report-accent) 7%, hsl(var(--background)))',
          backgroundImage: `
            linear-gradient(180deg, hsl(var(--background)) 0%, color-mix(in oklab, var(--report-accent) 6%, hsl(var(--background))) 28%, hsl(var(--background)) 72%),
            radial-gradient(circle at 12% 0%, color-mix(in oklab, var(--report-accent) 24%, transparent), transparent 42%),
            radial-gradient(circle at 88% 14%, color-mix(in oklab, var(--report-accent) 16%, transparent), transparent 38%)
          `,
        } as React.CSSProperties & Record<'--report-accent', string>)
      : undefined;

  return (
    <div
      className="min-h-svh bg-background pt-20 text-foreground sm:pt-24 md:pt-28 lg:pt-32"
      style={reportPageStyle}
    >
      <SEO
        title={document.seo.title}
        description={document.seo.description}
        image={getDocumentImage(document)}
        type={hasArticleMeta ? 'article' : 'website'}
        articleData={getArticleData(document)}
        gameData={getGameData(document)}
        breadcrumbs={breadcrumbs.map((item) => ({
          name: item.label,
          path: item.path,
        }))}
      />
      <main className="mx-auto flex w-full max-w-screen-2xl flex-col gap-3 px-4 pb-16 sm:gap-4 sm:px-6 md:px-8 lg:gap-8 lg:px-10 xl:px-12 2xl:px-16">
        <header className="flex flex-col gap-8">
          <Breadcrumbs items={breadcrumbs} />
          <DocumentHero document={document} />
          <Separator />
        </header>
        <div className={contentLayoutClassName}>
          {hasToc && <NavigationRail outline={outline} />}
          <div className="flex min-w-0 flex-col">
            <div className={hasArticleMeta ? 'pb-8' : undefined}>
              <DocumentSectionTree document={contentDocument} />
            </div>
            <ArticleMetaSection document={document} />
            {articleActionsSection?.widgets &&
              articleActionsSection.widgets.length > 0 && (
                <WidgetRenderer
                  widgets={articleActionsSection.widgets}
                  currentDocument={document}
                />
              )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentTemplate;
